import { AppThemeConstants } from './constants';
import { TabsProps } from '../components';
import { useEffect } from 'react';
import { CSSObject } from '@chakra-ui/react';
import { startCase } from 'lodash';

export const getBadgeStyles = (size: string): CSSObject => {
  switch (size) {
    case 'xs':
      return { px: 2, py: 0.5 };
    case 'sm':
      return { px: 2.5, py: 0.5 };
    case 'md':
      return { px: 3, py: 0.5 };
    case 'lg':
      return { px: 3.5, py: 0.5 };
    case 'xl':
      return { px: 4, py: 1 };
    default:
      return { px: 2.5, py: 0.5 };
  }
};

export const getIconStyles = (size: string): CSSObject => {
  switch (size) {
    case 'xs':
      return { h: 4, w: 4 };
    case 'sm':
      return { h: 4, w: 4 };
    case 'md':
      return { h: 4, w: 4 };
    case 'lg':
      return { h: 5, w: 5 };
    case 'xl':
      return { h: 6, w: 6 };
    default:
      return { h: 4, w: 4 };
  }
};

export const isPrimaryColor = (color: string) =>
  color === 'primary' || color === AppThemeConstants.PRIMARY_COLOR;

export const isSecondaryColor = (color: string) =>
  color === 'secondary' || color === AppThemeConstants.SECONDARY_COLOR;

export const isPrimaryOrSecondaryColor = (color: string) =>
  isPrimaryColor(color) || isSecondaryColor(color);

export const useOnClickOutside = (
  ref: React.RefObject<HTMLDivElement>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: { (event: any): void },
) => {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValueInArray = (value: any, array: any[]): boolean => {
  for (let i = 0; i < array.length; i++) {
    if (array[i] === value) {
      return true;
    }
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeValueFromArray = (value: any, array: any[]): any[] => {
  const index = array.indexOf(value);
  if (index > -1) {
    array.splice(index, 1);
  }
  return array;
};

export const bytesToSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 B';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1000)).toString());
  if (bytes < 1024) {
    const mbValue = bytes / Math.pow(1024, i);
    return (mbValue / 1024).toFixed(2) + ' KB';
  } else {
    return (
      (
        bytes / (i > 2 ? Math.pow(1024, i - 1) * 1000 : Math.pow(1024, i))
      ).toFixed(2) +
      ' ' +
      sizes[i]
    );
  }
};

export const getRandomNumbers = (arraySize: number) => {
  const arr = [];
  while (arr.length < arraySize) {
    const r = Math.floor(Math.random() * 100) + 1;
    if (arr.indexOf(r) === -1) {
      arr.push(r);
    }
  }
  return arr;
};

export const getOppositePosition = (position: TabsProps['tabPosition']) => {
  let direction = 'bottom';
  switch (position) {
    case 'bottom':
      direction = 'top';
      break;
    case 'left':
      direction = 'right';
      break;
    case 'right':
      direction = 'left';
      break;
    default:
      direction = 'bottom';
      break;
  }
  return startCase(direction);
};

export const generateId = (): string => {
  const d = Date.now() * 1000;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16 + d) % 16 | 0;

    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};
